import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { doc, setDoc, collection, GeoPoint, Timestamp } from 'firebase/firestore';
import { UserPlus, Mail, Lock, User, Phone, AlertCircle } from 'lucide-react';

// Mirrors iOS RestaurantType enum (Views/Auth/SignUpView "Business Type" picker)
const RESTAURANT_TYPES = [
  'Fast Food', 'Bar & Grill', 'Pizza', 'KFC', 'BBQ Grill', 'Seafood Restaurant',
  'Cafe', 'Beauty Supply', 'Retail Store', 'Farmer', 'Supermarket',
  'Fish/Meat Market', 'Asian', 'Indian', 'Bakery', 'Juice Stand',
  'Craft & Souvenirs', 'Liquor Store & Smoke Shop', 'Roti/Patty',
  'Perfume Store', 'Jewelry Store', 'Electronics Store', 'Home Goods',
  'Water Supply', 'Vegetarian', 'Snack Boxes', 'Car Parts', 'Pharmacy',
  'Latin', 'Mexican', 'Sushi', 'Steak House', 'Local Restaurant'
];

// iOS Parish enum (Models.swift) — pickup-zone source of truth for orders.
const PARISHES = [
  'Saint George Basseterre',
  'Saint Peter Basseterre',
  'Saint Anne Sandy Point',
  'Saint John Capisterre',
  'Christ Church Nichola Town',
  'Saint Paul Capisterre',
  'Saint Thomas Middle Island',
  'Saint Thomas Lowland',
  'Saint John Figtree',
  'Saint Paul Charlestown',
  'Saint George Gingerland',
  'Saint James Windward',
  "Saint Mary's Cayon",
  'Saint Thomas Trinity',
];

// Nevis has 5 parishes; the other 9 belong to St. Kitts.
const NEVIS_PARISHES = new Set([
  'Saint George Gingerland',
  'Saint James Windward',
  'Saint John Figtree',
  'Saint Paul Charlestown',
  'Saint Thomas Lowland',
]);

const parishesForIsland = (island) =>
  PARISHES.filter((p) => NEVIS_PARISHES.has(p) === (island === 'nevis'));

// iOS SignUpView picker: Customer / Driver / Business (no admin)
const ROLES = [
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'restaurant', label: 'Business' }
];

function SignUpPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'customer',
    restaurantType: 'Fast Food',
    island: 'saintKitts',
    parish: 'Saint George Basseterre'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const { db } = useFirebase();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      // Island switch resets parish to that island's first parish.
      ...(name === 'island' ? { parish: parishesForIsland(value)[0] } : {}),
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name');
      return;
    }

    setIsLoading(true);
    let user = null;

    try {
      // 1. Create Firebase Auth user (iOS AuthManager.signUp step 1)
      const userCredential = await register(formData.email, formData.password, `${formData.firstName} ${formData.lastName}`);
      user = userCredential?.user || userCredential;

      if (!user || !user.uid) {
        throw new Error('User creation failed - no valid user object');
      }

      const islandName = formData.island === 'saintKitts' ? 'St. Kitts' : 'Nevis';
      let newRestaurantId = null;

      // 2. Restaurant role: create the restaurant document first (iOS step 2)
      if (formData.role === 'restaurant') {
        const restaurantRef = doc(collection(db, 'restaurants'));
        newRestaurantId = restaurantRef.id;
        await setDoc(restaurantRef, {
          id: newRestaurantId,
          name: 'New Business',
          description: '',
          address: '',
          parish: formData.parish,
          island: islandName,
          phoneNumber: formData.phone.trim(),
          email: formData.email,
          openingHours: 'Mon-Sun: 10:00 AM - 10:00 PM',
          imageURL: '',
          isActive: true,
          isAcceptingOrders: true,
          isVerified: false,
          menuCategories: [],
          coordinates: new GeoPoint(0, 0),
          ownerId: user.uid,
          restaurantType: formData.restaurantType,
          createdAt: Timestamp.now()
        });
      }

      // 3. User document in users collection for all roles (iOS step 3)
      const userData = {
        uid: user.uid,
        email: formData.email,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        role: formData.role,
        isActive: true,
        seenByAdmin: false,
        island: islandName,
        parish: formData.parish,
        createdAt: Timestamp.now()
      };
      if (formData.role !== 'customer') {
        userData.hireDate = Timestamp.now();
        if (newRestaurantId) userData.restaurantId = newRestaurantId;
      }
      await setDoc(doc(db, 'users', user.uid), userData);

      // 4. Driver role: create the driver document (iOS step 4)
      if (formData.role === 'driver') {
        await setDoc(doc(db, 'drivers', user.uid), {
          id: user.uid,
          uid: user.uid, 
          email: formData.email,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phoneNumber: formData.phone.trim(),
          role: 'driver',
          isActive: true,
          isOnline: false,
          isAvailable: true,
          status: 'available',
          hireDate: Timestamp.now(),
          vehicleType: 'Car',
          vehicleNumber: '',
          licenseNumber: '',
          currentLocation: new GeoPoint(0, 0),
          lastKnownLocation: new GeoPoint(0, 0),
          totalDeliveries: 0,
          rating: 5.0,
          earnings: 0.0,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      }

      // Update Firebase Auth profile
      if (user && typeof user.updateProfile === 'function') {
        await user.updateProfile({
          displayName: `${formData.firstName} ${formData.lastName}`
        });
      }

      // Route by role — one platform, like iOS
      if (formData.role === 'restaurant') navigate('/business/welcome');
      else if (formData.role === 'driver') navigate('/driver/welcome');
      else navigate('/');
    } catch (err) {
      console.error('❌ Error creating account:', err);
      // iOS parity: if doc creation failed after auth user was created, clean up
      if (user && typeof user.delete === 'function') {
        try { await user.delete(); } catch (_) { /* best-effort cleanup */ }
      }
      setError(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 overflow-y-auto">
      <div className="max-w-md md:max-w-2xl w-full space-y-8">
        <div className="text-center fade-in">
          <img
            src="/ChefLogo.jpg"
            alt="CheF"
            className="w-[120px] h-[120px] rounded-full object-cover mx-auto mb-4 shadow-md ring-[3px] ring-[#FF9500]"
          />
          <h2 className="text-4xl font-bold gradient-text mb-2 text-center">Create Account</h2>
          <p className="text-gray-600 text-lg">
            {formData.role === 'driver'
              ? 'Join CheF and start earning on deliveries 🚗'
              : formData.role === 'restaurant'
                ? 'Join CheF and start selling to customers 🏪'
                : 'Join CheF and start ordering your favorite meals 👨‍🍳'}
          </p>
        </div>

        <div className="card p-8 slide-up">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-base font-bold text-gray-900 mb-2">
                    👤 First Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User size={20} className="text-orange-400" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                      placeholder="First name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-base font-bold text-gray-900 mb-2">
                    👤 Last Name
                  </label>
                  <div className="relative">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="modern-input w-full px-4 py-4 text-lg"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-base font-bold text-gray-900 mb-2">
                  📞 Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="(869) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-base font-bold text-gray-900 mb-2">
                  📧 Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {/* Account Type — mirrors iOS SignUpView "Account Type" section */}
              <div>
                <label htmlFor="role" className="block text-base font-bold text-gray-900 mb-2">
                  👥 Account Type
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="modern-input w-full px-4 py-4 text-lg"
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {formData.role === 'restaurant' && (
                <div>
                  <label htmlFor="restaurantType" className="block text-base font-bold text-gray-900 mb-2">
                    🏪 Business Type
                  </label>
                  <select
                    id="restaurantType"
                    name="restaurantType"
                    value={formData.restaurantType}
                    onChange={handleChange}
                    className="modern-input w-full px-4 py-4 text-lg"
                  >
                    {RESTAURANT_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Island Selection - Like iOS (parishes below filter by this) */}
              <div>
                <label htmlFor="island" className="block text-base font-bold text-gray-900 mb-2">
                  🏝️ Island 🇰🇳
                </label>
                <select
                  id="island"
                  name="island"
                  value={formData.island}
                  onChange={handleChange}
                  className="modern-input w-full px-4 py-4 text-lg"
                >
                  <option value="saintKitts">🇰🇳 St. Kitts</option>
                  <option value="nevis">🇰🇳 Nevis</option>
                </select>
              </div>

              {formData.role === 'restaurant' && (
                <div>
                  <label htmlFor="parish" className="block text-base font-bold text-gray-900 mb-2">
                    📍 Parish
                  </label>
                  <select
                    id="parish"
                    name="parish"
                    value={formData.parish}
                    onChange={handleChange}
                    className="modern-input w-full px-4 py-4 text-lg"
                  >
                    {parishesForIsland(formData.island).map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-base font-bold text-gray-900 mb-2">
                  🔒 Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="Create a password"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-base font-bold text-gray-900 mb-2">
                  🔒 Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

            </div>

            {error && (
              <div className="error-card p-4">
                <div className="error-icon">
                  <AlertCircle size={24} />
                </div>
                <p className="font-medium">{error}</p>
              </div>
            )}

            <div className="pt-12">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-4 text-lg font-bold shadow-2xl hover:shadow-orange-200/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="loading-spinner mr-3"></div>
                    <span>🍳 Creating account...</span>
                  </div>
                ) : (
                  <span>🎉 Create Account</span>
                )}
              </button>
            </div>

            <div className="text-center pt-4">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-bold text-orange-600 hover:text-orange-700 transition-colors">
                  🔐 Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Food decoration */}
        <div className="text-center text-4xl opacity-40 pb-8">
          🍗🍕🥗🍟
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
